<template>
  <section class="artists">
    <ArtistsTitleBox
      :sortData="sortData"
      :selectedSort="cardData.sort"
      :filters="filters"
      :search="search"
      :viewType="cardData.viewType"
      @sort="sortArtists"
      @open-filters="openFilters = $event"
      @select-filter="selectFilter"
      @apply-filters="refreshSelectedFilters"
      @clear-filters="refreshSelectedFilters(true)"
      @change-search="changeSearch"
      @update-view="updateView"
    />
    <div class="artists__nav">
      <router-link
        v-for="(item, index) in nav"
        :key="`nav-${index}`"
        :to="`/artists${item.hash}`"
        class="artists__nav-button"
        :class="{
          'artists__nav-button--white': item.active,
          'artists__nav-button--outline-white': !item.active,
        }"
      >
        {{ item.text }}</router-link
      >
    </div>
    <IconLoader v-if="loading" class="artists__loader loader--white loader--lg"></IconLoader>
    <template v-else>
      <template v-if="cardData.viewType === 'card'">
        <ArtistsGridView v-if="artists.length > 0" :artists="artists" />
        <h6 v-else class="exhibition__empty">NO ARTISTS</h6>
        <div v-if="showLoadMoreButton && hasMorePages" class="artists__button-box">
          <BaseButton
            class="button button--xs button--white"
            text="LOAD MORE"
            @click.native="handleLoadMoreClick"
          />
        </div>
      </template>
      <template v-else>
        <ArtistsListView v-if="sortedArtistsList.length > 0" :columns="sortedArtistsList" />
        <div v-if="sortedArtistsList.length > 0" class="artists__body"></div>
        <h6 v-else class="exhibition__empty">NO ARTISTS</h6>
      </template>
    </template>
    <div ref="infiniteTrigger" style="height: 1px"></div>
  </section>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from "vuex";
import BaseButton from "@/components/buttons/BaseButton.vue";
import IconLoader from "@/components/icons/IconLoader.vue";
import { sortedColumns } from "@/helper.js";
import useInfiniteScroll from "@/composables/useInfiniteScroll.js";
import ArtistsTitleBox from "@/views/public/artists/components/ArtistsTitleBox.vue";
import ArtistsGridView from "@/views/public/artists/components/ArtistsGridView.vue";
import ArtistsListView from "@/views/public/artists/components/ArtistsListView.vue";
import useSeoHead from "@/composables/useSeoHead";

export default {
  components: {
    BaseButton,
    IconLoader,
    ArtistsTitleBox,
    ArtistsGridView,
    ArtistsListView,
  },

  data() {
    return {
      sortData: [
        { id: "last_name_a_z", title: "Last name (A-Z)" },
        { id: "last_name_z_a", title: "Last name (Z-A)" },
      ],
      cardData: {
        sort: "last_name_a_z",
        page: 1,
        region: "",
        market: "",
        viewType: "list",
      },
      nav: [
        {
          active: true,
          text: "Our Artists",
          key: "",
          count: 0,
          hash: "#artists",
        },
        {
          active: false,
          text: "Secondary Market",
          key: "secondary",
          count: 0,
          hash: "#secondary-market",
        },
        {
          active: false,
          text: "All",
          key: "all",
          count: 0,
          hash: "#all",
        },
      ],
      navActive: {},
      openFilters: false,
      loading: false,
      sortedArtistsList: [],
      showLoadMoreButton: true,
      loadingMore: false,
    };
  },
  head() {
    return useSeoHead({
      title: "Artists we represent browngrotta arts",
      description: "Description Artists we represent browngrotta arts",
      image: "/img/icons/favicon-500x500.png",
      routePath: this.$route.path,
    });
  },
  watch: {
    $route: {
      handler: function (val, oldVal) {
        if (!val.hash) {
          this.$router.push("/artists#artists");
        } else this.clickNav(val.hash);
      },
      immediate: true,
      deep: true,
    },
  },
  async created() {
    this.loading = true;
    this.sortedArtistsList = sortedColumns(this.artists, this.getColumnsAmount); //to get columns
    await this.apiGetFiltersBlock({
      list: "regions",
      type: "content",
      search: this.search,
      isFirst: true,
    });

    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Artists",
        to: "/artists",
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);
    this.loading = false;
  },

  computed: {
    ...mapGetters("publicapi/artists", {
      artists: "getArtists",
      hasMorePages: "getHasMorePages",
    }),
    ...mapGetters("publicapi/filters", {
      filters: "getFilters",
      search: "getSearch",
      selectedFilters: "getSelectedFilters",
    }),
    getColumnsAmount() {
      const width = window.innerWidth;
      if (width < 540) {
        return 1;
      } else if (width < 768) {
        return 2;
      } else if (width < 1024) {
        return 3;
      } else {
        return 4;
      }
    },
  },

  methods: {
    ...mapActions("publicapi/artists", ["apiGetArtists"]),
    ...mapActions("publicapi/filters", ["apiGetFiltersBlock"]),
    ...mapMutations("publicapi/filters", ["updateFilters", "updateSelectedFilters", "clear"]),
    ...mapMutations(["setBreadcrumbs"]),

    async updateView(val) {
      this.viewType = val;
      let data = { ...this.cardData, page: val === "card" ? 1 : this.cardData.page, viewType: val };
      let success = await this.apiGetArtists(data);
      if (success) {
        this.cardData = { ...data };
        this.sortedArtistsList = sortedColumns(this.artists, this.getColumnsAmount); //to get columns
      }
    },

    async sortArtists(sort) {
      let data = { ...this.cardData, sort, page: 1 };
      let success = await this.apiGetArtists(data);
      if (success) {
        this.cardData = { ...data };
        this.sortedArtistsList = sortedColumns(this.artists, this.getColumnsAmount); //to get columns
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMorePages) return;
      this.loadingMore = true;

      let data = { ...this.cardData, page: this.cardData.page + 1 };
      await this.apiGetArtists(data);
      this.cardData = { ...data };
      this.sortedArtistsList = sortedColumns(this.artists, this.getColumnsAmount); //to get columns
      this.loadingMore = false;
    },
    handleLoadMoreClick() {
      this.showLoadMoreButton = false;
      this.loadMore();
      this.infiniteScroll.enableInfiniteScroll();
    },

    selectFilter(val) {
      this.updateFilters(val);
      if (val.apply) {
        this.refreshSelectedFilters();
      }
    },
    async refreshSelectedFilters(clear) {
      if (clear) {
        await this.clear();
      }
      await this.updateSelectedFilters();
      this.filterTable();
    },
    async filterTable() {
      this.loading = true;
      let data = { ...this.cardData, page: 1 };
      this.selectedFilters.forEach(el => {
        if (el.key === "price") {
          data.price_start = "";
          data.price_end = "";
          el.items.forEach(i => {
            data[i.key] = i.val;
          });
        } else {
          let filters = el.items.map(i => {
            return i.id;
          });
          data[el.key] = filters.join(",");
        }
      });
      let success = await this.apiGetArtists(data);
      this.loading = false;
      if (success) {
        this.cardData = data;
        this.sortedArtistsList = sortedColumns(this.artists, this.getColumnsAmount); //to get columns
      }
    },
    clickNav(hash) {
      this.nav.map(item => {
        if (item.hash === hash) {
          item.active = true;
          this.navActive = item;
          this.cardData.market = item.key;
          this.filterTable();
        } else item.active = false;
      });
    },

    async changeSearch(search) {
      await this.apiGetFiltersBlock({ list: "regions", type: "content", search });
    },
  },
  mounted() {
    this.infiniteScroll = useInfiniteScroll(this);
    if (!this.showLoadMoreButton) {
      this.infiniteScroll.enableInfiniteScroll();
    }
  },
  beforeDestroy() {
    this.infiniteScroll.disableInfiniteScroll();
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 1.8rem;
  line-height: 2.4rem;
  @media screen and (max-width: $xs) {
    font-size: 1.6rem;
    line-height: 2.5rem;
  }
}

.artists {
  @include artistsLayout;
  @media screen and (max-width: $xs) {
    margin-top: 15rem;
    padding: 0rem 2rem 11rem;
  }

  &__empty {
    @include emptyMessage(10px 10px 50px);
  }
  &__nav {
    padding: 6rem 0 4rem;
    display: flex;
    @media screen and (max-width: $xs) {
      width: 100%;
      padding: 45px 0 20px;
      flex-wrap: wrap;
    }
    &-button {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 19px;
      width: max-content;
      height: 4rem;
      border: 1px solid transparent;
      font-family: $fontFrutiger;
      font-weight: 500;
      font-size: 1.4rem;
      line-height: 1;
      letter-spacing: -0.4px;
      text-decoration: none;
      padding: 0 20px;
      margin-right: 10px;
      transition: all 0.3s ease;

      &--outline-white {
        background-color: transparent;
        color: $white;
        border-color: $white;
        &:hover {
          background-color: rgba($color: $white, $alpha: 0.1);
          color: $white;
        }
      }
      &--white {
        background-color: $white;
        color: $black;
        &:hover {
          background-color: $light2Grey;
        }
      }
      @media screen and (max-width: $xs) {
        height: 30px;
        padding: 0 15px;
        margin-bottom: 10px;
        font-size: 12px;
        text-transform: uppercase;
      }
      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  &__button-box {
    @include centeredButtonBox();
  }
  &__loader {
    @include centeredLoader;
  }
}
</style>

